import * as React from 'react';
import { ReportRecipientReport } from './models';
import { lastCommaFirstLocal } from '../project/survey/raters/module';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import DownloadIcon from '../icons/DownloadIcon';
import { COLORS } from '../globalStyle/colors';
import { TextDirection } from '../localization/textDirection';
import SendIcon from '../icons/SendIcon';
import { btnStyle } from '../forms/styles/button';
import Spacer from '../layout/Spacer';
import { css } from '@emotion/core';
import LocalDiv from '../localization/LocalDiv';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserAlt, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FLEX_ROW, FLEX_COLUMN } from '../layout/flexBox';
import { pathCombine } from '../core/api/module';
import { BaseUrl } from '../core/api/endpoints';
import { useMount } from '../core/hooks/useMount';
import { useBrowser } from '../language/useBrowser';


const styles = {
  deliverText:      css`
    color: ${COLORS.BLUE};
    font-size: 12px;
  `,
  link:             css`
    text-decoration: none;
    color: ${COLORS.BLUE};

    &:hover { 
      text-decoration: underline;
    }
  `,
  linkDisabled:       css`
    cursor: default;
    opacity: .6;

    &:hover { 
      text-decoration: none;
      cursor: default;
    }
  `,
  button:             css`
    color: ${COLORS.BLUE};

    &:hover { 
      text-decoration: underline;
      cursor: pointer;
    }
  `,
}

type Props = {
  className?: string;
  report: ReportRecipientReport;
  textDirection: TextDirection;
  hasDeliverPermission: boolean;
  deliverClicked: (report: ReportRecipientReport) => void;
  redeliverClicked: (report: ReportRecipientReport) => void;
  downloadClicked: () => void;
  reportClicked: (report: ReportRecipientReport, selected: boolean) => void;
  singleReport: boolean;
};

/**
 * ReportTableRow: a component that presents information about a report, as a table row
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ReportTableRow: React.FC<Props> = props => {
  const delivered  = React.useMemo(
    () => new Date(props.report.deliveryDate ?? new Date()),
    [props.report.deliveryDate],
  );

  const fileURL = (report: ReportRecipientReport) => {
    const baseUrl = pathCombine(BaseUrl, '/report/reportFile');
    return report?.fileExists ? baseUrl + '/' + encodeURIComponent(report!.reportFile) : undefined
  };

  const downloadURL = (report: ReportRecipientReport) => {
    const baseUrl = pathCombine(BaseUrl, '/report/reportDownload');
    return report?.fileExists ? baseUrl + '/' + encodeURIComponent(report!.reportFile) : undefined
  };

  useMount(() => {
    props.singleReport && props.reportClicked(props.report, true)
  });

  const { onIe } = useBrowser();
  
  return (
    <tr css={props.className}>
      <td>
        <LocalDiv css={FLEX_ROW}>
          <FontAwesomeIcon
            icon={props.report.isComposite ? faUsers : faUserAlt}
          />
          <Spacer sizePx={5} css={FLEX_COLUMN}/>
          <LocalDiv css={FLEX_COLUMN}>
            {lastCommaFirstLocal(props.textDirection, props.report)}
          </LocalDiv>
        </LocalDiv>
      </td>
      <td>
        {
          props.report.isComposite
          ? <Msg str={Str.NA} />
          : props.report.email
        }
      </td>
      <td>
        {delivered.toLocaleDateString()}&nbsp;{delivered.toLocaleTimeString()}
      </td>
      <td>
        {
          props.report.isComposite
          ? <Msg str={Str.NA} />
          : props.report.portalDelivered
            ? <LocalDiv css={FLEX_ROW}>
                <Msg str={Str.Yes} css={FLEX_COLUMN}/>
                <Spacer sizePx={5} css={FLEX_COLUMN} />
                <span css={FLEX_COLUMN}>(</span>
                <button 
                  type='button'
                  onClick={() => props.redeliverClicked(props.report)}
                  css={[FLEX_COLUMN, btnStyle.invisible, styles.button]}
                >
                  <Msg str={Str.Redeliver} css={[FLEX_COLUMN, styles.deliverText]}/>
                </button>                  
                <span css={FLEX_COLUMN}>)</span>
              </LocalDiv>
            : props.hasDeliverPermission
            ? <button 
                type='button'
                onClick={() => props.deliverClicked(props.report)}
                css={[btnStyle.invisible, styles.button]}
              >
                <LocalDiv css={FLEX_ROW}>
                  <Msg str={Str.Deliver} css={[FLEX_COLUMN, styles.deliverText]}/>
                  <Spacer sizePx={5} css={FLEX_COLUMN} />
                  <SendIcon sizePx={14} fill={COLORS.BLUE} css={FLEX_COLUMN} />
                </LocalDiv>
              </button>
              : <Msg str={Str.No} />
        }
      </td>
      <td>
        { props.report.webReport
          ? <a
              href={props.report.reportUrl}
              target={'_blank'}
              rel='noopener noreferrer'
              css={styles.link}
            >
              <Msg 
                str={props.report.isComposite ? Str.Composite : props.report.isSummary ? Str.Summary: Str.Individual}
              />
            </a>          
          : onIe
            ? <Msg 
                str={props.report.isComposite ? Str.Composite : props.report.isSummary ? Str.Summary: Str.Individual}
              />
            : <a
                href={fileURL(props.report)}
                target={'_blank'}
                rel='noopener noreferrer'
                css={[styles.link, !props.report.fileExists && styles.linkDisabled]}
              >
                <Msg 
                  str={props.report.isComposite ? Str.Composite : props.report.isSummary ? Str.Summary: Str.Individual}
                />
              </a>
        }
      </td>
      <td>
        <a 
          href={downloadURL(props.report)}
          css={[btnStyle.invisible, styles.button, !props.report.fileExists && styles.linkDisabled]}
          onClick={() => props.downloadClicked}
        >
          <DownloadIcon sizePx={20} fill={COLORS.BLUE} />
        </a>        
      </td>
      <td>
        <input 
          type='checkbox'
          checked={props.report.isChecked || props.singleReport || false}
          onChange={(event) => !props.singleReport && props.reportClicked(props.report, event.target.checked)}
          disabled={props.singleReport}
        />
      </td>
    </tr>
  )
};

export default ReportTableRow;
